import { PaymentSecuritiesService } from '../services/payment-securities.service';
import { Component, OnInit, Input, Output, EventEmitter, ViewEncapsulation, OnDestroy } from '@angular/core';
import { AppEventDispatcher } from '../../../utils/AppEventDispatcher';
import { TweenMax, Quint } from 'gsap';
import { PaymentSecuritiesEventType } from '../payment-securities.event.type';
import { Payment } from '../models/payment';
import { CurrentBalanceEventType } from '../../../shared/current-balance/current-balance.event.type';
import { ListItem } from '../../../shared/list-confirmation/model/list-item';
import * as moment from 'moment';
import { FormatPricePipe } from '../../../shared/format-price.pipe';
import { CpfCnpjPipe } from '../../../shared/cpf-cnpj.pipe';
import { EventTypes } from '../../../shared/EventTypes';
import { Auth } from '../../../autentication/Auth';
import { Util } from '../../../utils/util';
import { PaymentResponseStatus } from '../models/payment-response.enum';
import { PaymentAntifraudeModel } from '../models/payment-antifraude.model';
import { PaymentResponseHandlerService } from '../services/payment-response-handler';

@Component({
	selector: 'app-payment-result',
	templateUrl: './payment-result.component.html',
	styleUrls: ['./payment-result.component.styl'],
	encapsulation: ViewEncapsulation.None
})

export class PaymentResultComponent implements OnInit, OnDestroy {
	@Input() flow: string;
	@Input() submited = false;
	@Input() paymentData: Payment;
	@Output() setTitleSubtitle: EventEmitter<any> = new EventEmitter<any>();
	@Output() showStepButtons: EventEmitter<boolean> = new EventEmitter<boolean>();

	private view = '[data-component="payment-result"]';
	private antifraudeView = '[data-component="payment-antifraud-warning"]';
	private confirmList: Array<ListItem> = [];
	public isSecurityCip = false;
	public isSecuritieAgreement = false;
	public active = false;
	public isScheduled: boolean;
	public showExportButtons: boolean;
	public isCipPayment = false;
	public antifraudeModel: PaymentAntifraudeModel;

	constructor(private paymentSecuritiesService: PaymentSecuritiesService, private paymentResponseHandler: PaymentResponseHandlerService,) {}

	ngOnInit() {
		AppEventDispatcher.listen(
			PaymentSecuritiesEventType.STEPS.PAYMENT_RESULT,
			data => {
				if (data.show) {
					this.isSecurityCip = data.isSecuritieCip;
					this.isSecuritieAgreement = data.securitieAgreement;

					if(!this.awaitingAntifraudAnalysis && !this.antifraudCanceled) {
						this.active = true;
						this.showStepButtons.emit(true);
					}

					this.show();
				} else {
					this.hide();
				}
			}
		);
	}

	ngOnDestroy() {
		AppEventDispatcher.removeListener(PaymentSecuritiesEventType.STEPS.PAYMENT_RESULT);
	}

	buildConfirmList() {
		const FP = new FormatPricePipe();
		const CP = new CpfCnpjPipe();
		this.confirmList = [];
		this.confirmList.push(new ListItem('Código de barras:', this.paymentData.barCode, 'cls_mask'));
		this.confirmList.push(new ListItem('Valor do pagamento:', FP.transform(this.paymentData.value, false), 'cls_mask'));
		this.confirmList.push(new ListItem('Data de pagamento:', moment(this.paymentData.dateLabel).format('DD/MM/YYYY')));
		if (this.paymentData.assignorName !== undefined) {
			this.confirmList.push(new ListItem('Nome do beneficiário:', this.paymentData.assignorName, 'cls_mask'));
		}
		if (this.paymentData.assignorCpfCnpj !== undefined) {
			this.confirmList.push(new ListItem('Cpf/Cnpj do beneficiário:', Util.maskCpfCnpjPix(this.paymentData.assignorCpfCnpj), 'cls_mask'));
		}
		this.confirmList.push(new ListItem('Agência:', String(this.paymentData.payerAgency), 'cls_mask'));
		this.confirmList.push(new ListItem('Conta:', String(this.paymentData.payerAccount), 'cls_mask'));
		this.confirmList.push(new ListItem('Nome do pagador:', Util.camelCaseText(this.paymentData.payerName), 'cls_mask'));
		this.confirmList.push(new ListItem('Cpf/Cnpj do pagador:', Util.maskCpfCnpjPix(this.paymentData.payerCpfCnpj), 'cls_mask'));
		if (this.paymentData.nominalValue) {
			this.confirmList.push(new ListItem('Valor do documento:', FP.transform(this.paymentData.nominalValue, false), 'cls_mask'));
		} else {
			this.confirmList.push(new ListItem('Valor do documento:', FP.transform(this.paymentData.value, false), 'cls_mask'));
		}
		this.confirmList.push(new ListItem('Abatimento do título:', FP.transform(this.paymentData.securitieRebateValue, false), 'cls_mask'));
		this.confirmList.push(new ListItem('Desconto:', FP.transform(this.paymentData.discountValue, false), 'cls_mask'));
		this.confirmList.push(new ListItem('Juros / Mora:', FP.transform(this.paymentData.taxValue, false), 'cls_mask'));
		this.confirmList.push(new ListItem('Multa:', FP.transform(this.paymentData.fineValue, false), 'cls_mask'));
		this.paymentData.chargesValue ? this.confirmList.push(new ListItem('Encargos:', FP.transform(this.paymentData.chargesValue, false), 'cls_mask')) : '';

		this.confirmList.push(new ListItem('Vencimento:', moment(this.paymentData.dueDateInput).format('DD/MM/YYYY')));
		this.paymentData.resumeList = this.confirmList;
	}

	buildAgreementConfirmList() {
		this.confirmList = [];
		this.confirmList.push(new ListItem('Código de barras:', this.paymentData.barCode, 'cls_mask'));
		this.confirmList.push(new ListItem('Beneficiário:', this.paymentData.paymentNameAgreement, 'cls_mask'));
		const FP = new FormatPricePipe();
		this.confirmList.push(new ListItem('Valor:', FP.transform(this.paymentData.value, false), 'cls_mask'));
		this.confirmList.push(new ListItem('Data de pagamento:', moment(this.paymentData.dateLabel).format('DD/MM/YYYY')));
		this.confirmList.push(new ListItem('Nome do pagador:', Util.camelCaseText(Auth.getInstance().getUser().name ? Auth.getInstance().getUser().name : Auth.getInstance().getUser().loggedUserName), 'cls_mask'));
		this.confirmList.push(new ListItem('Cpf/Cnpj do pagador:', Util.maskCpfCnpjPix(Auth.getInstance().getUser().cpfCnpj ? Auth.getInstance().getUser().cpfCnpj : Auth.getInstance().getUser().cpf), 'cls_mask'));
		this.paymentData.resumeList = this.confirmList;
	}

	get awaitingAntifraudAnalysis(): boolean {
		return this.paymentData.situation === PaymentResponseStatus.AGUARDANDO_ANALISE_ANTIFRAUDE;
	}


	get antifraudCanceled(): boolean {
		return this.paymentData.situation === PaymentResponseStatus.CANCELADA_SUSPEITA_FRAUDE;
	}

	setDataAntifraudScreen(): void {
		this.antifraudeModel = this.paymentResponseHandler.getAntifraudModel();
	}

	show() {
		this.isCipPayment = this.paymentData.codeBarCip;

		if (this.awaitingAntifraudAnalysis) {
			this.showStepButtons.emit(false);
			this.setTitleSubtitle.emit({
				title: 'Importante',
				subtitle: 'Para sua segurança, a transferência abaixo está em análise!',
			});
			this.setDataAntifraudScreen();
			this.setIntervalAntifraude();
			return;
		}

		if (moment(this.paymentData.date).format('DD/MM/YYYY') !== moment(this.paymentData.dateLabel).format('DD/MM/YYYY')
			&& !this.paymentSecuritiesService.scheduleSpan) {
			this.isScheduled = true;
		} else {
			this.isScheduled = this.paymentSecuritiesService.scheduleSpan;
		}

		this.setTitleSubtitle.emit({
			title: 'Resumo de Pagamento de Títulos',
			subtitle: 'Pagamento de boletos, contas de luz, água e afins.'
		});

		if (this.isSecuritieAgreement) {
			this.buildAgreementConfirmList();
		} else {
			this.buildConfirmList();
		}

		AppEventDispatcher.dispatch(PaymentSecuritiesEventType.BUTTONS, {
			show: false
		});

		const interval = setInterval(() => {
			if ($(this.view).length > 0) {
				const containerHeight = this.getStepHeight();

				TweenMax.to('.steps-container', 0.8, {
					height: containerHeight,
					ease: Quint.easeOut
				});
				TweenMax.to(this.view, 0.8, {
					delay: 0.4,
					left: 0,
					ease: Quint.easeOut
				});
				AppEventDispatcher.dispatch(
					CurrentBalanceEventType.BALANCE_UPDATE,
					null
				);

				clearInterval(interval);
			}
		}, 10);

		TweenMax.delayedCall(.5, () => {
			const containerHeight = this.getStepHeight();

			TweenMax.to('.steps-container', .8, { height: containerHeight, ease: Quint.easeOut});
			TweenMax.to(this.view, .8, { delay: .4, left: 0, ease: Quint.easeOut });
			AppEventDispatcher.dispatch( CurrentBalanceEventType.BALANCE_UPDATE, null);

			AppEventDispatcher.dispatch(EventTypes.EXPORT_BUTTONS.DATA_TRANSACTION, {
				transferNumber: this.paymentData.confirmationCode,
				receiptType: this.paymentData.paymentType
			});
		});
	}

	setIntervalAntifraude() {
		const interval = setInterval(() => {
			if ($(this.antifraudeView).length > 0) {
				const containerHeight = this.getStepHeight();

				TweenMax.to('.steps-container', 1, {
					height: containerHeight,
					ease: Quint.easeOut
				});
				TweenMax.to(this.antifraudeView, 1, {
					delay: 0.5,
					left: 0,
					ease: Quint.easeOut
				});
				AppEventDispatcher.dispatch(
					CurrentBalanceEventType.BALANCE_UPDATE,
					null
				);

				clearInterval(interval);
			}
		}, 10);

		TweenMax.delayedCall(.5, () => {
			const containerHeight = this.getStepHeight();

			TweenMax.to('.steps-container', .8, { height: containerHeight, ease: Quint.easeOut});
			TweenMax.to(this.antifraudeView, .8, { delay: .8, left: 0, ease: Quint.easeOut });
			AppEventDispatcher.dispatch( CurrentBalanceEventType.BALANCE_UPDATE, null);
		});
	}

	hide() {
		TweenMax.to(this.view, 2.8, {
			left: '-100%',
			ease: Quint.easeOut,
			onComplete: () => {
				this.active = false;
			}
		});
		TweenMax.to(this.antifraudeView, 2.8, {
			left: '-100%',
			ease: Quint.easeOut,
			onComplete: () => {
				this.paymentResponseHandler.clearAntifraudeModel();
				this.antifraudeModel = this.paymentResponseHandler.getAntifraudModel();
			}
		});
	}

	getStepHeight(): number {
		if (this.awaitingAntifraudAnalysis) {
			return $(this.antifraudeView).outerHeight() + $('.buttons').outerHeight() + 160;
		}

		return $(this.view).outerHeight() + $('.buttons').outerHeight() + 160;
	}

	emitPrint = () => window.print();
}



// WEBPACK FOOTER //
// /home/ubuntu/workdir/workspace/Front End Angular (NGINX)/omni-internetbanking/dev-omni-internetbanking-deploy/src/app/master/payment-securities/payment-result/payment-result.component.ts